import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { hasActiveCreatorProfile } from '../lib/creatorPresentation';

/**
 * OneToOneVipRequiredDialog — Phase 45
 * ------------------------------------------------------------------
 * Shown when a non-VIP user clicks the "One-to-One" action card on the
 * Live Dashboard. Explains why the feature is gated and funnels the
 * user into the Creator Onboarding Wizard. Zero side-effects: the
 * dialog does NOT change wallet, availability or any backend state.
 */
const OneToOneVipRequiredDialog = ({ open, onOpenChange }) => {
  const { t } = useTranslation('lives');
  const navigate = useNavigate();
  const { user } = useAuth();
  const alreadyActive = hasActiveCreatorProfile(user);
  const testPrefix = alreadyActive ? 'o2o-service-unavailable' : 'o2o-vip-required';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid={`${testPrefix}-dialog`}
      >
        <DialogHeader>
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-amber-500/15 gold-text">
            <Sparkles className="h-5 w-5" />
          </div>
          <DialogTitle className="gold-text" data-testid={`${testPrefix}-title`}>
            {alreadyActive ? t('creator_presentation.unavailable_title', { ns: 'common' }) : t('dashboard.o2o_vip_required.title')}
          </DialogTitle>
          <DialogDescription data-testid={`${testPrefix}-desc`}>
            {alreadyActive ? t('creator_presentation.unavailable_description', { ns: 'common' }) : t('dashboard.o2o_vip_required.description')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-2 flex-row justify-end gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid={`${testPrefix}-cancel`}
          >
            {t('dashboard.o2o_vip_required.cancel')}
          </Button>
          <Button
            type="button"
            className="gold-gradient text-black"
            onClick={() => { onOpenChange(false); navigate(alreadyActive ? '/contact' : '/creator/onboarding'); }}
            data-testid={`${testPrefix}-cta`}
          >
            {alreadyActive ? t('creator_presentation.contact_support', { ns: 'common' }) : t('dashboard.o2o_vip_required.cta')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OneToOneVipRequiredDialog;
